import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { useProperty } from '@/lib/property/propertyContextDef';
import { OwnerOnly } from '@/lib/auth/OwnerOnly';
import {
  buildReservationPatch,
  DAY_KEYS,
  DAY_LABELS,
  formFromRow,
  useReservationSettings,
  useUpdateReservationSettings,
  type ReservationSettingsForm,
  type SimpleHoursForm,
} from '@/lib/data/reservations';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/reservations')({
  component: ReservationsPage,
});

function ReservationsPage() {
  return (
    <OwnerOnly>
      <ReservationsContent />
    </OwnerOnly>
  );
}

function ReservationsContent() {
  const { supabase } = useAuth();
  const { businessId } = useProperty();
  const settingsQuery = useReservationSettings(supabase, businessId);

  if (settingsQuery.isLoading) return <PageLoading />;
  if (settingsQuery.isError || !settingsQuery.data) {
    return <p className="text-body text-danger">Could not load your reservation settings.</p>;
  }
  if (settingsQuery.data.category !== 'restaurant') {
    return (
      <section className="rounded-card border border-border bg-surface p-xl shadow-card">
        <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Reservations</h1>
        <p className="mt-sm text-body text-ink-2">Table reservations are only for restaurants.</p>
      </section>
    );
  }

  // Keyed on the business so switching property re-seeds the form.
  return <ReservationsForm key={businessId} initial={formFromRow(settingsQuery.data)} />;
}

function ReservationsForm({ initial }: { initial: { form: ReservationSettingsForm; hoursLocked: boolean } }) {
  const { supabase } = useAuth();
  const { businessId } = useProperty();
  const update = useUpdateReservationSettings(supabase, businessId);
  const [form, setForm] = useState(initial.form);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const { hoursLocked } = initial;

  const change = (patch: Partial<ReservationSettingsForm>) => {
    setSaved(false);
    setForm((f) => ({ ...f, ...patch }));
  };
  const changeHours = (patch: Partial<SimpleHoursForm>) => change({ hours: { ...form.hours, ...patch } });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    const built = buildReservationPatch(form, hoursLocked);
    if ('invalid' in built) {
      setError(built.invalid);
      return;
    }
    update.mutate(built.patch, {
      onSuccess: () => setSaved(true),
      onError: (err) => setError(err instanceof Error ? err.message : 'Could not save.'),
    });
  }

  const inputClass = 'rounded-input border border-border bg-surface px-sm py-xs text-body text-ink';

  return (
    <section className="mx-auto flex max-w-2xl flex-col gap-lg">
      <h1 className="text-screen-title font-extrabold tracking-tight text-ink">Reservations</h1>

      <form onSubmit={handleSubmit} className="flex flex-col gap-lg rounded-card border border-border bg-surface p-lg shadow-card">
        <fieldset className="flex flex-col gap-sm">
          <legend className="text-h3 font-bold text-ink">When you take tables</legend>
          {hoursLocked ? (
            <p className="text-body text-ink-2">
              Your service times are set up per day, which this page cannot edit without losing detail. They are left
              exactly as they are — contact support to change them.
            </p>
          ) : (
            <>
              <p className="text-body text-ink-2">
                Leave this blank and guests see our default lunch and dinner times. Set it and they only see yours —
                and days you are closed offer nothing at all.
              </p>
              <div className="flex flex-wrap gap-sm" role="group" aria-label="Days you take reservations">
                {DAY_KEYS.map((d) => {
                  const open = form.hours.openDays.includes(d);
                  return (
                    <label
                      key={d}
                      className={`flex cursor-pointer items-center gap-xs rounded-button border px-sm py-xs text-body ${
                        open ? 'border-canyon bg-canyon text-white' : 'border-border text-ink'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={open}
                        onChange={() =>
                          changeHours({
                            openDays: open ? form.hours.openDays.filter((x) => x !== d) : [...form.hours.openDays, d],
                          })
                        }
                      />
                      {DAY_LABELS[d]}
                    </label>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-md">
                <label className="flex flex-col gap-xs text-caption text-ink-2">
                  Lunch from
                  <input type="time" value={form.hours.lunchFrom} onChange={(e) => changeHours({ lunchFrom: e.target.value })} className={inputClass} />
                </label>
                <label className="flex flex-col gap-xs text-caption text-ink-2">
                  Lunch until
                  <input type="time" value={form.hours.lunchTo} onChange={(e) => changeHours({ lunchTo: e.target.value })} className={inputClass} />
                </label>
              </div>
              <div className="flex flex-wrap gap-md">
                <label className="flex flex-col gap-xs text-caption text-ink-2">
                  Dinner from
                  <input type="time" value={form.hours.dinnerFrom} onChange={(e) => changeHours({ dinnerFrom: e.target.value })} className={inputClass} />
                </label>
                <label className="flex flex-col gap-xs text-caption text-ink-2">
                  Dinner until
                  <input type="time" value={form.hours.dinnerTo} onChange={(e) => changeHours({ dinnerTo: e.target.value })} className={inputClass} />
                </label>
              </div>
              <p className="text-caption text-ink-2">The last table is offered 30 minutes before each window ends.</p>
            </>
          )}
        </fieldset>

        <div className="flex flex-wrap gap-md">
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Largest party you'll take (optional)
            <input
              inputMode="numeric"
              value={form.maxParty}
              onChange={(e) => change({ maxParty: e.target.value.replace(/\D/g, '') })}
              placeholder="e.g. 12"
              className={inputClass}
            />
          </label>
          <label className="flex flex-col gap-xs text-caption text-ink-2">
            Notice you need, in hours (optional)
            <input
              inputMode="numeric"
              value={form.leadHours}
              onChange={(e) => change({ leadHours: e.target.value.replace(/\D/g, '') })}
              placeholder="e.g. 2"
              className={inputClass}
            />
          </label>
        </div>

        <label className="flex items-start gap-sm text-body text-ink">
          <input
            type="checkbox"
            checked={form.autoAccept}
            onChange={(e) => change({ autoAccept: e.target.checked })}
            className="mt-xs"
          />
          <span>
            <strong>Confirm tables automatically</strong>
            <span className="mt-xs block text-ink-2">
              On: a request that fits your hours, party size and notice is confirmed straight away, and the guest is
              told. Anything else still waits for you. Off: you accept every table yourself.
            </span>
          </span>
        </label>

        {error ? <p className="text-body text-danger">{error}</p> : null}
        {saved ? <p className="text-body text-success">Saved.</p> : null}
        <button
          type="submit"
          disabled={update.isPending}
          className="self-start rounded-button bg-canyon px-lg py-sm text-body font-bold text-white disabled:opacity-50"
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
    </section>
  );
}
